import React, { useMemo } from 'react';
import { resizedUrl, RewardsOffersDTO, useSpringBigStore } from '@springbig/shared';
import { VStack, HStack, Image, ScrollView, Progress } from 'native-base';
import { Caption } from '~/shared/Typography';
import { useAppNavigate } from '~/pages';
import { faAward, faStar } from '@fortawesome/free-solid-svg-icons';
import { SpIcon } from '~/shared/Icon';
import { Button, Card } from '~/shared/Theme';
import { useTheme } from '@springbig/shared';

type RewardsProps = {
    data: RewardsOffersDTO;
};

export const Rewards = ({
    data,
}: RewardsProps) => {

    const { navigate, params } = useAppNavigate();
    const { member, setParamId } = useSpringBigStore();

    const {
        radius_sm,
        radius_md,
        primary,
        background_secondary,
    } = useTheme();

    const redeemableRewards = useMemo(() => {

        return member?.rewards
            ?.filter((reward) => reward?.item_value <= member.balance)
            ?.sort((a, b) => b?.item_value - a?.item_value);
    }, [JSON.stringify(member?.rewards)]);

    const lowestTierReward = useMemo(() => {

        return member?.rewards ? [...member.rewards].sort((a, b) => a?.item_value - b?.item_value)?.[0] : null;
    }, [JSON.stringify(member?.rewards)]);

    const pointsNeeded = useMemo(() => {

        return lowestTierReward && lowestTierReward?.item_value
            ? Math.max(0, lowestTierReward?.item_value - member.balance)
            : 0;
    }, [lowestTierReward, member.balance]);

    return (
        <VStack space={2}>
            <HStack justifyContent={'space-between'} alignItems={'center'}>
                <HStack space={2} alignItems={'center'}>
                    <SpIcon icon={faAward} size={24} color={primary}/>
                    <Caption
                        text={'homepage.rewards.title'}
                        fontWeight={'bold'}
                    />
                </HStack>
                <Button
                    variant={'text'}
                    modifier={'small'}
                    text={'homepage.rewards.viewAll'}
                    onPress={() => navigate(`w/${params.wallet_id}/rewards`)}
                />
            </HStack>
            { pointsNeeded > 0 &&
                <Card alignItems={'center'} space={1}>
                    <Caption text={'homepage.rewards.noRewards'} bold />
                    <Progress
                        value={(member && member.balance / lowestTierReward.item_value) * 100}
                        size={'sm'}
                        width={'80%'}
                        rounded={radius_sm}
                        bg={background_secondary}
                        _filledTrack={{ bg: primary }}
                    />
                    <Caption
                        text={'homepage.rewards.pointsStatus'}
                        textValues={[member && member.balance, pointsNeeded]}
                    />
                </Card>
            }

            <ScrollView horizontal showsHorizontalScrollIndicator={true}>
                <HStack space={2} pb={2}>
                    { redeemableRewards.map((reward) => (

                        <Card
                            key={reward.id}
                            p={2}
                            alignItems={'center'}
                            justifyContent={'space-between'}
                            width={'124px'}
                            space={2}
                        >
                            <VStack space={2} w={'full'}>
                                <Caption fontWeight="bold">
                                    { reward?.item_value } points
                                </Caption>
                                { reward?.images[0]?.url ?
                                    <Image
                                        src={resizedUrl(reward?.images[0]?.url, 108, 108)}
                                        w={'100%'}
                                        rounded={radius_md}
                                        style={{
                                            aspectRatio: 1,
                                        }}
                                    />
                                    :
                                    <Card
                                        secondary
                                        w={'full'}
                                        justifyContent={'center'}
                                    >
                                        <SpIcon
                                            color={primary}
                                            icon={faStar}
                                            size={80}
                                        />
                                    </Card>
                                }
                            </VStack>
                            <VStack space={2} w={'full'}>
                                <Caption textAlign={'center'}>
                                    {
                                        reward.item_name.length > 32 ?
                                            (reward.item_name.substring(0, 32) + '...')
                                            :
                                            reward.item_name
                                    }
                                </Caption>
                                { data.current_points >= reward.item_value &&
                                    <Button
                                        text={'homepage.rewards.redeem'}
                                        onPress={() => {

                                            setParamId(reward.id);
                                            navigate(`w/${params.wallet_id}/rewards/${reward.id}`);
                                        }}
                                    />
                                }
                            </VStack>
                        </Card>
                    ))}
                </HStack>
            </ScrollView>
        </VStack>
    );
};
